import React, { useState, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import axios from 'axios';
import TimezoneUtils from '../utils/timezoneUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

const Dashboard = ({ userRole: propUserRole, currentUserId: propCurrentUserId, teamMembers: propTeamMembers, preloaded }) => {
  const [bidData, setBidData] = useState({});
  const [selectedMember, setSelectedMember] = useState('all');
  const [teamMembers, setTeamMembers] = useState([]);
  const [dateRange, setDateRange] = useState('14');
  const [resumeName, setResumeName] = useState(''); // Add resume name state
  const [resumeNameFilter, setResumeNameFilter] = useState(''); // Add separate state for the actual filter value
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memberColors, setMemberColors] = useState({});
  const [profileColors, setProfileColors] = useState({});
  const [userRole, setUserRole] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');

  // Use props if provided, otherwise fetch data
  useEffect(() => {
    if (preloaded && propUserRole && propCurrentUserId && propTeamMembers) {
      // Use preloaded data
      setUserRole(propUserRole);
      setCurrentUserId(propCurrentUserId);
      
      // Show all team members to everyone (no filtering)
      const filteredMembers = propTeamMembers;
      
      setTeamMembers(filteredMembers);
      
      // Generate colors for team members
      const colors = {};
      filteredMembers.forEach(member => {
        colors[member._id] = getRandomColor();
      });
      setMemberColors(colors);
      
      // Set default selected member
      if (propUserRole === 'lead') {
        // Leads see 'all' by default
        setSelectedMember('all');
      } else {
        // Non-leads default to their own applications
        setSelectedMember(propCurrentUserId);
      }
    } else {
      // Fetch current user info and role (original logic)
      const fetchCurrentUser = async () => {
        try {
          const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`);
          setUserRole(userResponse.data.role);
          setCurrentUserId(userResponse.data._id);
        } catch (error) {
          console.error('Error fetching current user:', error);
          setError('Error fetching user information');
        }
      };

      fetchCurrentUser();
    }
  }, [preloaded, propUserRole, propCurrentUserId, propTeamMembers]);

  useEffect(() => {
    // Only fetch team members if not preloaded
    if (preloaded) return;

    const fetchTeamMembers = async () => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`);
        const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`);
        
        // Show all team members to everyone (no filtering)
        const filteredMembers = response.data;
        
        setTeamMembers(filteredMembers);
        
        // Generate and store a color for each team member
        const colors = {};
        filteredMembers.forEach(member => {
          colors[member._id] = getRandomColor();
        });
        setMemberColors(colors);
        
        // Set default selected member
        if (userResponse.data.role === 'lead') {
          // Leads see 'all' by default
          setSelectedMember('all');
        } else {
          // Non-leads default to their own applications
          setSelectedMember(userResponse.data._id);
        }
      } catch (error) {
        setError('Error fetching team members: ' + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMembers();
  }, [preloaded]);

  useEffect(() => {
    // Fetch bid history when member, date range, or resume name filter changes
    const fetchBidHistory = async () => {
      if (!currentUserId) return;

      setError(null);
      setIsLoading(true);
      try {
        if (userRole === 'lead' && selectedMember === 'all') {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/applications/bid-history-batch`,
            {
              params: {
                userIds: teamMembers.map((member) => member._id).join(','),
                days: dateRange,
                resumeName: resumeNameFilter.trim() || undefined,
              },
            }
          );
          setBidData(response.data || {});
        } else {
          // Fetch data for single member (either selected member or current user)
          const memberId = selectedMember === 'all' ? currentUserId : selectedMember;
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/applications/bid-history/${memberId}`, {
            params: { 
              days: dateRange,
              resumeName: resumeNameFilter.trim() || undefined // Only include if not empty
            }
          });
          setBidData({ [memberId]: response.data });
        }
      } catch (error) {
        setError('Error fetching bid history: ' + (error.response?.data?.message || error.message));
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have the necessary data
    if (currentUserId && (teamMembers.length > 0 || userRole !== 'lead')) {
      fetchBidHistory();
    }
  }, [selectedMember, dateRange, resumeNameFilter, teamMembers, userRole, currentUserId]); // Use resumeNameFilter instead of resumeName

  // Handle Enter key press for resume name input
  const handleResumeNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setResumeNameFilter(resumeName); // Apply the filter when Enter is pressed
    }
  };

  // Handle input change for resume name
  const handleResumeNameChange = (e) => {
    setResumeName(e.target.value);
  };

  // Generate random color for each member
  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  // Generate color from profile name (consistent colors for same profile)
  const getProfileColor = (profileName) => {
    if (!profileColors[profileName]) {
      const newColor = getRandomColor();
      setProfileColors(prev => ({ ...prev, [profileName]: newColor }));
      return newColor;
    }
    return profileColors[profileName];
  };

  // Transform backend data for stacked chart
  const transformBidDataForChart = (rawData, selectedProfiles = null) => {
    if (!rawData || !rawData.dates || rawData.dates.length === 0) {
      return { labels: [], datasets: [] };
    }

    const datesData = rawData.dates;
    const allProfiles = rawData.profiles || [];
    
    // Filter profiles if specified (for multi-user view)
    const profilesToShow = selectedProfiles || allProfiles;

    // Generate complete date range to fill in missing dates
    const rangeDays = parseInt(dateRange, 10);
    const now = TimezoneUtils.now();
    const startDate = TimezoneUtils.addDaysUTC(now, -rangeDays);
    const endDate = now;
    const allDates = TimezoneUtils.generateDateRange(TimezoneUtils.startOfDayUTC(startDate), TimezoneUtils.startOfDayUTC(endDate));

    // Create a map of existing data for quick lookup
    const dataMap = {};
    datesData.forEach(entry => {
      dataMap[entry.date] = entry;
    });

    // Fill in all dates (including missing ones with 0)
    // Format labels with day of week
    const labels = allDates.map(date => TimezoneUtils.formatDateWithDayOfWeek(date));

    // Create datasets for each profile
    const datasets = profilesToShow.map(profile => {
      const color = getProfileColor(profile);
      return {
        label: profile,
        data: allDates.map(date => {
          const entry = dataMap[date];
          return entry ? (entry[profile] || 0) : 0;
        }),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });

    return { labels, datasets };
  };

  // Prepare chart data for stacked columns
  const chartData = useMemo(() => {
    if (selectedMember === 'all' && userRole === 'lead' && Object.keys(bidData).length > 0) {
      // Multi-user view: combine all profiles from all users
      const allProfilesSet = new Set();
      Object.values(bidData).forEach(userData => {
        if (userData && userData.profiles) {
          userData.profiles.forEach(profile => allProfilesSet.add(profile));
        }
      });
      const allProfiles = Array.from(allProfilesSet).sort();

      // Generate complete date range to fill in missing dates
      const rangeDays = parseInt(dateRange, 10);
      const now = TimezoneUtils.now();
      const startDate = TimezoneUtils.addDaysUTC(now, -rangeDays);
      const endDate = now;
      const allDates = TimezoneUtils.generateDateRange(TimezoneUtils.startOfDayUTC(startDate), TimezoneUtils.startOfDayUTC(endDate));

      // Create a map of all users' data for quick lookup
      const allUserDataMaps = {};
      Object.entries(bidData).forEach(([userId, userData]) => {
        if (userData && userData.dates) {
          const dataMap = {};
          userData.dates.forEach(entry => {
            dataMap[entry.date] = entry;
          });
          allUserDataMaps[userId] = dataMap;
        }
      });

      // Format labels with day of week
      const labels = allDates.map(date => TimezoneUtils.formatDateWithDayOfWeek(date));
      const datasets = allProfiles.map(profile => {
        // Sum up counts across all members for this profile for each date
        const data = allDates.map(date => {
          let total = 0;
          Object.values(allUserDataMaps).forEach(dataMap => {
            const entry = dataMap[date];
            if (entry) {
              total += entry[profile] || 0;
            }
          });
          return total;
        });

        const color = getProfileColor(profile);
        return {
          label: profile,
          data: data,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1
        };
      });

      return { labels, datasets };
    } else {
      // Single user view: show profiles for selected member
      const memberId = selectedMember === 'all' ? currentUserId : selectedMember;
      const userData = bidData[memberId];
      if (!userData) {
        return { labels: [], datasets: [] };
      }
      return transformBidDataForChart(userData);
    }
  }, [bidData, selectedMember, userRole, currentUserId, profileColors, dateRange]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: userRole === 'lead' && selectedMember === 'all' 
          ? 'Team Bid History by Profile (Stacked)' 
          : 'Bid History by Profile (Stacked)'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        filter: (tooltipItem) => {
          // Hide profiles with 0 counts in tooltip
          return tooltipItem.parsed.y > 0;
        },
        callbacks: {
          footer: (tooltipItems) => {
            // Calculate total for this date (only from visible items)
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            return `Total: ${total}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Date'
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Bids'
        }
      }
    }
  };

  return (
    <div className="dashboard-container">
      <h1>Team Bid History Dashboard</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="dashboard-controls">
        {teamMembers.length > 0 && (
          <select 
            value={selectedMember}
            onChange={(e) => setSelectedMember(e.target.value)}
            className="member-select"
          >
            {userRole === 'lead' && <option value="all">All Members</option>}
            {teamMembers.map(member => (
              <option key={member._id} value={member._id}>
                {member.name} {member.role === 'bidder' ? '(Bidder)' : member.role === 'lead' ? '(Lead)' : '(Member)'}
              </option>
            ))}
          </select>
        )}

        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="date-range-select"
          disabled={isLoading}
        >
          <option value="7">Last 7 Days</option>
          <option value="14">Last 14 Days</option>
          <option value="30">Last 30 Days</option>
        </select>

        {/* Add resume name input with Enter key handling */}
        <input
          type="text"
          value={resumeName}
          onChange={handleResumeNameChange}
          onKeyPress={handleResumeNameKeyPress}
          placeholder="Filter by resume name... (Press Enter to apply)"
          className="resume-name-input"
          disabled={isLoading}
        />
      </div>

      <div className="chart-container" style={{ minHeight: '400px', height: '500px' }}>
        {isLoading ? (
          <p>Loading...</p>
        ) : Object.keys(bidData).length > 0 && chartData.labels.length > 0 ? (
          <Bar data={chartData} options={chartOptions} />
        ) : !isLoading ? (
          <p>No bid data available for the selected period</p>
        ) : null}
      </div>

      <style jsx>{`
        .dashboard-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .dashboard-controls {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          align-items: center;
        }

        .member-select,
        .date-range-select,
        .resume-name-input {
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #ccc;
          min-width: 200px;
        }

        .resume-name-input {
          min-width: 300px;
        }

        .user-info {
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #ccc;
          min-width: 200px;
          background-color: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
        }

        .chart-container {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .error-message {
          color: #dc3545;
          padding: 10px;
          margin-bottom: 20px;
          border-radius: 4px;
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
